import { createTheme, Theme } from "@mui/material/styles";
import { getContrastRatio } from "@mui/material/styles";
import { AtemTheme } from "../components/Theme";
import { BrandConfig, ThemeMode } from "./types";

const AA_NORMAL = 4.5; // razão mínima WCAG AA p/ texto normal

// Aviso dev-time: cor de marca cujo contraste com o contrastText calculado não atinge AA.
// Não quebra a app; apenas sinaliza má config. Fora de produção.
function warnLowContrast(brandId: string, theme: Theme): void {
  if (process.env.NODE_ENV === "production") return;
  (["primary", "secondary"] as const).forEach((key) => {
    const { main, contrastText } = theme.palette[key];
    if (contrastText && getContrastRatio(main, contrastText) < AA_NORMAL) {
      // eslint-disable-next-line no-console
      console.warn(
        `[branding] marca "${brandId}": contraste ${key} abaixo de AA (${AA_NORMAL}:1).`
      );
    }
  });
}

// Deriva o tema MUI de uma marca+modo. Reusa AtemTheme() como base estrutural (component
// overrides, typography, breakpoints), sobrepondo só palette e tipografia da marca.
// Memoização fica no provider (por brand.id+mode); aqui a função é pura.
export function createBrandTheme(brand: BrandConfig, mode: ThemeMode): Theme {
  const base = AtemTheme();

  // Default reproduz exatamente o visual atual (RN04); dark só vira palette.mode.
  if (brand.id === "default") {
    return mode === "dark"
      ? createTheme(base, { palette: { mode: "dark" } })
      : base;
  }

  // Marca: usa o esquema do modo; sem dark, herda primary/secondary do light (P3).
  const scheme = mode === "dark" ? brand.schemes.dark ?? brand.schemes.light : brand.schemes.light;

  // augmentColor deriva light/dark/contrastText a partir da COR DA MARCA (respeitando os
  // valores informados na config), não do deep-merge que deixava os derivados da Atem —
  // corrige o hover vermelho da Atem nos botões contained das telas de marca.
  const primary = base.palette.augmentColor({ color: scheme.primary });
  const secondary = base.palette.augmentColor({ color: scheme.secondary });

  const theme = createTheme(base, {
    palette: {
      mode,
      primary,
      secondary,
      ...(scheme.background ? { background: scheme.background } : {}),
    },
    ...(brand.typography?.fontFamily
      ? { typography: { fontFamily: brand.typography.fontFamily } }
      : {}),
    ...(brand.shape?.borderRadius != null
      ? { shape: { borderRadius: brand.shape.borderRadius } }
      : {}),
  });

  warnLowContrast(brand.id, theme);
  return theme;
}
